import { DownloadOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, Form, Space, Table, Typography, Row, Col, Statistic, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from '../../config/axios';
import CopyableText from '../../components/copyable-text';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface OverdueReportData {
	device_id: number;
	customer_name: string;
	customer_phone: string;
	sold_by_retailer_name: string;
	sold_by_retailer_vendor_id: string;
	total_amount_to_be_paid: number;
	amount_paid: number;
	balance_amount: number;
	overdue_amount: number;
	overdue_installment_count: number;
	total_pending_installments: number;
	total_penalty_amount: number;
	overdue_days: number;
}

interface OverdueReportTotals {
	device_id: string;
	customer_name: string;
	sold_by_retailer_name: string;
	sold_by_retailer_vendor_id: string;
	total_amount_to_be_paid: number;
	amount_paid: number;
	balance_amount: number;
	overdue_amount: number;
	overdue_installment_count: number;
	total_penalty_amount: number;
	overdue_days: number;
}

interface OverdueReportResponse {
	devices: OverdueReportData[];
	totals: OverdueReportTotals;
	count: number;
	page: number;
	limit: number;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const ugx = (v: number) => `UGX ${formatNumber(v)}`;

const getSeverityColor = (days: number) => {
	if (days <= 7) return 'orange';
	if (days <= 14) return 'red';
	return 'purple';
};

const getSeverityLabel = (days: number) => {
	if (days <= 7) return 'Warning';
	if (days <= 14) return 'Critical';
	return 'Severe';
};

const quickPresets: { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] = [
	{ label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
	{ label: '14 Days', value: [dayjs().subtract(14, 'day'), dayjs()] },
	{ label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
	{ label: 'All Time', value: [dayjs('2020-01-01'), dayjs()] },
];

const OverdueReport = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({ page: 1, pageSize: 20 });
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs().startOf('year'), dayjs()],
	});

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['overdue-report', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};
			const response = await axios.get('/admin/reports/overdue', { withCredentials: true, params });
			return response.data.results as OverdueReportResponse;
		},
	});

	if (error) handleAPIError(error);

	const overdueData: OverdueReportData[] = data?.devices || [];
	const totals = data?.totals;

	const handleFilter = () => {
		setFilters(form.getFieldsValue());
		setPaginationConfig({ ...paginationConfig, page: 1 });
	};

	const handleReset = () => {
		const defaultRange: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().startOf('year'), dayjs()];
		form.setFieldsValue({ date_range: defaultRange });
		setFilters({ date_range: defaultRange });
		setPaginationConfig({ page: 1, pageSize: 20 });
	};

	const handleExport = () => {
		if (!overdueData.length) return;
		const sheet = XLSX.utils.json_to_sheet(
			overdueData.map((r) => ({
				'Device ID': r.device_id,
				'Customer Name': r.customer_name,
				'Customer Phone': r.customer_phone,
				'Retailer': r.sold_by_retailer_name,
				'Retailer ID': r.sold_by_retailer_vendor_id,
				'Total Payable (UGX)': r.total_amount_to_be_paid,
				'Amount Paid (UGX)': r.amount_paid,
				'Balance (UGX)': r.balance_amount,
				'Overdue Amount (UGX)': r.overdue_amount,
				'Penalty (UGX)': r.total_penalty_amount,
				'Overdue Installments': r.overdue_installment_count,
				'Total Pending Installments': r.total_pending_installments,
				'Overdue Days': r.overdue_days,
				'Severity': getSeverityLabel(r.overdue_days),
			}))
		);
		sheet['!cols'] = [
			{ wch: 10 }, { wch: 22 }, { wch: 16 }, { wch: 26 }, { wch: 12 },
			{ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 12 },
		];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, sheet, 'Overdue');
		const start = filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : 'all';
		const end = filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : 'now';
		XLSX.writeFile(wb, `overdue-report-${start} to ${end}.xlsx`);
	};

	const collectionRate =
		totals && totals.total_amount_to_be_paid > 0
			? ((totals.amount_paid / totals.total_amount_to_be_paid) * 100).toFixed(1)
			: '0.0';

	const columns: ColumnsType<OverdueReportData> = [
		{
			title: 'Device ID',
			dataIndex: 'device_id',
			key: 'device_id',
			width: 90,
			sorter: (a, b) => a.device_id - b.device_id,
		},
		{
			title: 'Customer',
			key: 'customer',
			width: 180,
			render: (_, r) => (
				<div>
					<div className="font-medium">{r.customer_name}</div>
					<div className="text-xs text-gray-500">{r.customer_phone}</div>
				</div>
			),
		},
		{
			title: 'Retailer',
			key: 'retailer_info',
			width: 220,
			render: (_, record) => (
				<div className="flex flex-col gap-1">
					<div className="font-medium">{record.sold_by_retailer_name}</div>
					<CopyableText text={record.sold_by_retailer_vendor_id}>
						<span className="text-xs text-gray-500">ID: {record.sold_by_retailer_vendor_id}</span>
					</CopyableText>
				</div>
			),
		},
		{
			title: 'Total Payable',
			dataIndex: 'total_amount_to_be_paid',
			key: 'total_amount_to_be_paid',
			width: 160,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_amount_to_be_paid - b.total_amount_to_be_paid,
		},
		{
			title: 'Collected',
			dataIndex: 'amount_paid',
			key: 'amount_paid',
			width: 150,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.amount_paid - b.amount_paid,
		},
		{
			title: 'Balance',
			dataIndex: 'balance_amount',
			key: 'balance_amount',
			width: 150,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.balance_amount - b.balance_amount,
		},
		{
			title: 'Overdue Amount',
			dataIndex: 'overdue_amount',
			key: 'overdue_amount',
			width: 160,
			align: 'right',
			render: (v: number) => (
				<Text strong style={{ color: '#ff4d4f' }}>
					{ugx(v)}
				</Text>
			),
			sorter: (a, b) => a.overdue_amount - b.overdue_amount,
			defaultSortOrder: 'descend',
		},
		{
			title: 'Penalty',
			dataIndex: 'total_penalty_amount',
			key: 'total_penalty_amount',
			width: 150,
			align: 'right',
			render: (v: number) => <Text style={{ color: '#ff7875' }}>{ugx(v)}</Text>,
			sorter: (a, b) => a.total_penalty_amount - b.total_penalty_amount,
		},
		{
			title: 'Overdue / Pending',
			key: 'overdue_installment_count',
			width: 140,
			align: 'center',
			render: (_, r) => (
				<Tag color="red">
					{r.overdue_installment_count} / {r.total_pending_installments}
				</Tag>
			),
			sorter: (a, b) => a.overdue_installment_count - b.overdue_installment_count,
		},
		{
			title: 'Overdue Days',
			dataIndex: 'overdue_days',
			key: 'overdue_days',
			width: 160,
			align: 'center',
			render: (v: number) => (
				<Tag color={getSeverityColor(v)}>
					{v} days ({getSeverityLabel(v)})
				</Tag>
			),
			sorter: (a, b) => a.overdue_days - b.overdue_days,
		},
	];

	return (
		<AppLayout title="Overdue Report" subtitle="Overdue payments tracking and analysis">
			{/* Summary Cards */}
			{totals && (
				<Card className="mb-6">
					<Title level={5} className="mb-4">
						Overdue Summary
					</Title>
					<Row gutter={[16, 16]}>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Devices"
								value={data?.count || 0}
								valueStyle={{ color: '#1890ff' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Overdue Installments"
								value={totals.overdue_installment_count}
								valueStyle={{ color: '#ff4d4f' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Overdue Amount"
								value={totals.overdue_amount}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#ff4d4f' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Penalty"
								value={totals.total_penalty_amount}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#ff7875' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Balance"
								value={totals.balance_amount}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#faad14' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Tooltip title="Amount collected vs total payable across all overdue devices">
								<Statistic
									title="Collection Rate"
									value={collectionRate}
									suffix="%"
									valueStyle={{ color: parseFloat(collectionRate) >= 50 ? '#52c41a' : '#ff4d4f' }}
								/>
							</Tooltip>
						</Col>
					</Row>
				</Card>
			)}

			{/* Filters */}
			<Card className="mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<Form
						form={form}
						layout="inline"
						initialValues={{ date_range: [dayjs().startOf('year'), dayjs()] }}
					>
						<Form.Item name="date_range" label="Date Range">
							<RangePicker style={{ width: 280 }} placeholder={['Start Date', 'End Date']} />
						</Form.Item>
					</Form>
					<Space wrap>
						{quickPresets.map((p) => (
							<Button
								key={p.label}
								size="small"
								onClick={() => {
									form.setFieldsValue({ date_range: p.value });
									setFilters({ date_range: p.value });
									setPaginationConfig({ ...paginationConfig, page: 1 });
								}}
							>
								{p.label}
							</Button>
						))}
					</Space>
					<Space>
						<Button type="primary" icon={<FilterOutlined />} onClick={handleFilter}>
							Apply
						</Button>
						<Button icon={<ReloadOutlined />} onClick={handleReset}>
							Reset
						</Button>
						<Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!overdueData.length}>
							Export
						</Button>
						<Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
							Refresh
						</Button>
					</Space>
				</div>
			</Card>

			{/* Data Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={overdueData}
					loading={isLoading}
					rowKey={(record) => `${record.device_id}`}
					scroll={{ x: 1600 }}
					pagination={{
						current: paginationConfig.page,
						pageSize: paginationConfig.pageSize,
						total: data?.count || 0,
						onChange: (page, pageSize) => setPaginationConfig({ page, pageSize: pageSize || 20 }),
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} overdue devices`,
					}}
				/>
			</Card>
		</AppLayout>
	);
};

export default OverdueReport;
